import React from 'react';

const FUTURES_ALERTS_KEY = 'tradournal_futures_alerts';

function loadStorage(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch { return fallback; }
}

function toNum(v, fb = 0) {
  const n = parseFloat(v);
  return isFinite(n) ? n : fb;
}

const CATEGORIES = [
  {
    label: 'Equity Indices',
    color: '#388bfd',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
      </svg>
    ),
    contracts: [
      { symbol: 'ES=F', name: 'S&P 500' },
      { symbol: 'NQ=F', name: 'Nasdaq 100' },
      { symbol: 'YM=F', name: 'Dow Jones' },
      { symbol: 'RTY=F', name: 'Russell 2000' },
    ],
  },
  {
    label: 'Energy',
    color: '#f97316',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
      </svg>
    ),
    contracts: [
      { symbol: 'CL=F', name: 'Crude Oil WTI' },
      { symbol: 'BZ=F', name: 'Brent Crude' },
      { symbol: 'NG=F', name: 'Natural Gas' },
      { symbol: 'HO=F', name: 'Heating Oil' },
      { symbol: 'RB=F', name: 'RBOB Gasoline' },
    ],
  },
  {
    label: 'Metals',
    color: '#eab308',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/><path d="M12 6v6l4 2"/>
      </svg>
    ),
    contracts: [
      { symbol: 'GC=F', name: 'Gold' },
      { symbol: 'SI=F', name: 'Silver' },
      { symbol: 'PL=F', name: 'Platinum' },
      { symbol: 'PA=F', name: 'Palladium' },
      { symbol: 'HG=F', name: 'Copper' },
    ],
  },
  {
    label: 'Agricultural',
    color: '#22c55e',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22V12"/><path d="M5 12C5 7 8 3 12 3c4 0 7 4 7 9"/><path d="M5 12c2 0 4-1 7-1s5 1 7 1"/>
      </svg>
    ),
    contracts: [
      { symbol: 'ZW=F', name: 'Wheat' },
      { symbol: 'ZC=F', name: 'Corn' },
      { symbol: 'ZS=F', name: 'Soybeans' },
      { symbol: 'ZO=F', name: 'Oats' },
      { symbol: 'KC=F', name: 'Coffee' },
      { symbol: 'SB=F', name: 'Sugar' },
      { symbol: 'CT=F', name: 'Cotton' },
    ],
  },
  {
    label: 'Currencies & Rates',
    color: '#a78bfa',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/><path d="M9 9h6"/><path d="M9 12h6"/><path d="M9 15h4"/>
      </svg>
    ),
    contracts: [
      { symbol: 'DX=F', name: 'US Dollar Index' },
      { symbol: '6E=F', name: 'Euro FX' },
      { symbol: '6J=F', name: 'Japanese Yen' },
      { symbol: 'ZB=F', name: '30-Year T-Bond' },
      { symbol: 'ZN=F', name: '10-Year T-Note' },
    ],
  },
];

const ALL_SYMBOLS = CATEGORIES.flatMap(c => c.contracts.map(x => x.symbol));

// Yahoo Finance symbol → TradingView symbol mapping
const TV_SYMBOL = {
  'ES=F': 'CME_MINI:ES1!',
  'NQ=F': 'CME_MINI:NQ1!',
  'YM=F': 'CBOT_MINI:YM1!',
  'RTY=F': 'CME_MINI:RTY1!',
  'CL=F': 'NYMEX:CL1!',
  'BZ=F': 'NYMEX:BZ1!',
  'NG=F': 'NYMEX:NG1!',
  'HO=F': 'NYMEX:HO1!',
  'RB=F': 'NYMEX:RB1!',
  'GC=F': 'COMEX:GC1!',
  'SI=F': 'COMEX:SI1!',
  'PL=F': 'NYMEX:PL1!',
  'PA=F': 'NYMEX:PA1!',
  'HG=F': 'COMEX:HG1!',
  'ZW=F': 'CBOT:ZW1!',
  'ZC=F': 'CBOT:ZC1!',
  'ZS=F': 'CBOT:ZS1!',
  'ZO=F': 'CBOT:ZO1!',
  'KC=F': 'ICEUS:KC1!',
  'SB=F': 'ICEUS:SB1!',
  'CT=F': 'ICEUS:CT1!',
  'DX=F': 'TVC:DXY',
  '6E=F': 'CME:6E1!',
  '6J=F': 'CME:6J1!',
  'ZB=F': 'CBOT:ZB1!',
  'ZN=F': 'CBOT:ZN1!',
};

function ChartModal({ contract, quotes, C, onClose }) {
  const tvSymbol = TV_SYMBOL[contract.symbol] || contract.symbol;
  const q = quotes[contract.symbol];
  const hasData = q && !isNaN(q.price);
  const up = hasData && q.changePct >= 0;
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/tv.js';
    script.async = true;
    script.onload = () => {
      if (window.TradingView && containerRef.current) {
        new window.TradingView.widget({
          container_id: 'tv-chart-container',
          symbol: tvSymbol,
          interval: 'D',
          timezone: 'America/New_York',
          theme: 'dark',
          style: '1',
          locale: 'en',
          toolbar_bg: '#0f1725',
          enable_publishing: false,
          allow_symbol_change: false,
          hide_side_toolbar: false,
          width: '100%',
          height: '100%',
        });
      }
    };
    document.head.appendChild(script);
    return () => {
      try { document.head.removeChild(script); } catch {}
    };
  }, [tvSymbol]);

  // Close on Escape
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column' }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#0f1725', borderBottom: '1px solid #1e2d45', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={{ color: '#e8eefb', fontWeight: 800, fontSize: 16 }}>{contract.name}</div>
            <div style={{ color: '#5a7499', fontSize: 11, fontFamily: 'monospace' }}>{tvSymbol}</div>
          </div>
          {hasData && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ color: '#e8eefb', fontWeight: 800, fontSize: 20, fontFamily: 'monospace' }}>
                {q.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ color: up ? '#0ecb81' : '#ef4444', fontWeight: 700, fontSize: 13 }}>
                {up ? '+' : ''}{q.changePct.toFixed(2)}%
              </div>
              <div style={{ color: '#5a7499', fontSize: 11 }}>H: {q.high.toFixed(2)} · L: {q.low.toFixed(2)}</div>
            </div>
          )}
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#e8eefb', borderRadius: 8, cursor: 'pointer', padding: '8px 16px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Close
        </button>
      </div>
      <div ref={containerRef} id="tv-chart-container" style={{ flex: 1, minHeight: 0 }} />
    </div>
  );
}

async function sendPush(userId, title, body, tag) {
  if (!userId) return;
  try {
    await fetch('/api/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send', userId, title, body, tag }),
    });
  } catch {}
}

export default function FuturesPanel({ C, cardStyle, inp, userId }) {
  const [quotes, setQuotes] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [alerts, setAlerts] = React.useState(() => loadStorage(FUTURES_ALERTS_KEY, []));
  const [alertForm, setAlertForm] = React.useState({ symbol: '', direction: 'above', price: '' });
  const [showAlertForm, setShowAlertForm] = React.useState(false);
  const [firedAlerts, setFiredAlerts] = React.useState(new Set());
  const [activeCategory, setActiveCategory] = React.useState('all');
  const [selectedChart, setSelectedChart] = React.useState(null);

  React.useEffect(() => { localStorage.setItem(FUTURES_ALERTS_KEY, JSON.stringify(alerts)); }, [alerts]);

  const fetchQuotes = React.useCallback(async () => {
    try {
      const res = await fetch(`/api/gap-scanner?symbols=${encodeURIComponent(ALL_SYMBOLS.join(','))}`);
      const json = await res.json();
      const rows = json?.quoteResponse?.result || [];
      const next = {};
      rows.forEach(q => {
        next[q.symbol] = {
          price: toNum(q.regularMarketPrice, NaN),
          change: toNum(q.regularMarketChange, NaN),
          changePct: toNum(q.regularMarketChangePercent, NaN),
          high: toNum(q.regularMarketDayHigh, NaN),
          low: toNum(q.regularMarketDayLow, NaN),
          volume: q.regularMarketVolume,
          open: toNum(q.regularMarketOpen, NaN),
        };
      });
      setQuotes(next);
      setLastUpdated(new Date());
      return next;
    } catch { return null; }
  }, []);

  React.useEffect(() => {
    setLoading(true);
    fetchQuotes().finally(() => setLoading(false));
    const id = setInterval(fetchQuotes, 60000);
    return () => clearInterval(id);
  }, [fetchQuotes]);

  // Check alerts
  React.useEffect(() => {
    if (Object.keys(quotes).length === 0) return;
    alerts.forEach(alert => {
      if (firedAlerts.has(alert.id)) return;
      const q = quotes[alert.symbol];
      if (!q || isNaN(q.price)) return;
      const triggered =
        (alert.direction === 'above' && q.price >= toNum(alert.price)) ||
        (alert.direction === 'below' && q.price <= toNum(alert.price));
      if (triggered) {
        setFiredAlerts(prev => new Set([...prev, alert.id]));
        const title = `Futures Alert: ${alert.symbol}`;
        const body = `${alert.symbol} is ${alert.direction} ${alert.price} — now at ${q.price.toFixed(2)}`;
        if (Notification.permission === 'granted') {
          new Notification(title, { body, icon: '/favicon.ico' });
        }
        sendPush(userId, title, body, `futures-${alert.symbol}-${alert.id}`);
      }
    });
  }, [quotes, alerts, firedAlerts]);

  const addAlert = () => {
    if (!alertForm.symbol || !alertForm.price) return;
    setAlerts(prev => [...prev, { id: Date.now(), ...alertForm, created: new Date().toISOString() }]);
    setAlertForm(f => ({ ...f, price: '' }));
    setShowAlertForm(false);
    if (Notification.permission === 'default') Notification.requestPermission();
  };

  const removeAlert = (id) => setAlerts(prev => prev.filter(a => a.id !== id));

  const labelStyle = { color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' };

  const visibleCategories = activeCategory === 'all'
    ? CATEGORIES
    : CATEGORIES.filter(c => c.label === activeCategory);

  const activeAlerts = alerts.filter(a => !firedAlerts.has(a.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {selectedChart && <ChartModal contract={selectedChart} quotes={quotes} C={C} onClose={() => setSelectedChart(null)} />}

      {/* Header bar */}
      <div style={{ ...cardStyle, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 15, letterSpacing: '-0.2px' }}>Futures Market</div>
            <div style={{ color: C.sub, fontSize: 11, marginTop: 2 }}>
              {loading ? 'Fetching prices…' : lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Live delayed data · auto-refreshes every 60s'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {activeAlerts.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(234,179,8,0.12)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 5, padding: '3px 9px' }}>
                {activeAlerts.length} alert{activeAlerts.length !== 1 ? 's' : ''} active
              </span>
            )}
            <button
              onClick={() => { setLoading(true); fetchQuotes().finally(() => setLoading(false)); }}
              style={{ padding: '7px 14px', background: C.card2, border: `1px solid ${C.border}`, color: C.sub, borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              Refresh
            </button>
            <button
              onClick={() => setShowAlertForm(v => !v)}
              style={{ padding: '7px 14px', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', color: '#eab308', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              Set Alert
            </button>
          </div>
        </div>

        {/* Category filter tabs */}
        <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
          {['all', ...CATEGORIES.map(c => c.label)].map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 11, fontWeight: 700, border: '1px solid',
                borderColor: activeCategory === cat ? (CATEGORIES.find(c => c.label === cat)?.color || C.blue) : C.border,
                background: activeCategory === cat ? `${CATEGORIES.find(c => c.label === cat)?.color || C.blue}18` : C.card2,
                color: activeCategory === cat ? (CATEGORIES.find(c => c.label === cat)?.color || C.blue) : C.sub,
              }}
            >
              {cat === 'all' ? 'All' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Alert form */}
      {showAlertForm && (
        <div style={{ ...cardStyle, padding: '18px 20px', border: '1px solid rgba(234,179,8,0.3)' }}>
          <div style={{ ...labelStyle, color: '#eab308', marginBottom: 14 }}>New Price Alert</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Contract</div>
              <select value={alertForm.symbol} onChange={e => setAlertForm(f => ({ ...f, symbol: e.target.value }))} style={{ ...inp }}>
                <option value="">Select...</option>
                {CATEGORIES.map(cat => (
                  <optgroup key={cat.label} label={cat.label}>
                    {cat.contracts.map(c => <option key={c.symbol} value={c.symbol}>{c.name} ({c.symbol})</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Condition</div>
              <select value={alertForm.direction} onChange={e => setAlertForm(f => ({ ...f, direction: e.target.value }))} style={{ ...inp }}>
                <option value="above">Price above</option>
                <option value="below">Price below</option>
              </select>
            </div>
            <div>
              <div style={{ ...labelStyle, marginBottom: 6 }}>Price</div>
              <input
                type="number"
                value={alertForm.price}
                onChange={e => setAlertForm(f => ({ ...f, price: e.target.value }))}
                placeholder="0.00"
                style={{ ...inp }}
              />
            </div>
            <button onClick={addAlert} style={{ padding: '10px 18px', background: '#eab308', color: '#000', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
              + Add
            </button>
          </div>
        </div>
      )}

      {/* Active alerts summary */}
      {alerts.length > 0 && (
        <div style={{ ...cardStyle, padding: '14px 20px' }}>
          <div style={{ ...labelStyle, marginBottom: 10 }}>Active Alerts</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {alerts.map(a => {
              const fired = firedAlerts.has(a.id);
              const q = quotes[a.symbol];
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: fired ? 'rgba(14,203,129,0.1)' : 'rgba(234,179,8,0.1)', border: `1px solid ${fired ? 'rgba(14,203,129,0.3)' : 'rgba(234,179,8,0.3)'}`, color: fired ? C.greenBright : '#eab308' }}>
                  {fired ? '✓' : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>}
                  {a.symbol} {a.direction === 'above' ? '↑' : '↓'} {a.price}
                  {q && !isNaN(q.price) && <span style={{ opacity: 0.7 }}>· now {q.price.toFixed(2)}</span>}
                  <button onClick={() => removeAlert(a.id)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14, padding: 0, opacity: 0.6, lineHeight: 1 }}>×</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Category cards */}
      {visibleCategories.map(cat => (
        <div key={cat.label} style={{ ...cardStyle, padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: `${cat.color}18`, border: `1px solid ${cat.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat.color }}>
              {cat.icon}
            </div>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 13, letterSpacing: '-0.1px' }}>{cat.label}</div>
          </div>

          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 110px 100px 90px 90px', gap: 8, padding: '6px 10px', borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
            {['Contract', 'Symbol', 'Price', 'Change', 'High', 'Low'].map(h => (
              <div key={h} style={{ color: C.sub, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</div>
            ))}
          </div>

          {cat.contracts.map((contract, i) => {
            const q = quotes[contract.symbol];
            const hasData = q && !isNaN(q.price);
            const up = hasData && q.changePct >= 0;
            const alertsForSym = alerts.filter(a => a.symbol === contract.symbol);
            return (
              <div key={contract.symbol}>
                <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 110px 100px 90px 90px', gap: 8, padding: '11px 10px', alignItems: 'center', borderRadius: 6, background: i % 2 === 0 ? 'transparent' : `${C.card2}44`, cursor: 'pointer' }}
                  onClick={() => setSelectedChart(contract)}
                >
                  <div style={{ fontWeight: 700, color: C.text, fontSize: 13 }}>{contract.name}</div>
                  <div style={{ color: C.sub, fontSize: 11, fontFamily: 'monospace' }}>{contract.symbol}</div>
                  <div style={{ fontWeight: 800, color: C.text, fontSize: 14, fontFamily: 'monospace' }}>
                    {hasData ? q.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span style={{ color: C.sub }}>—</span>}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 12, color: hasData ? (up ? C.greenBright : C.redBright) : C.sub }}>
                    {hasData ? `${up ? '+' : ''}${q.changePct.toFixed(2)}%` : '—'}
                  </div>
                  <div style={{ color: C.sub, fontSize: 11 }}>{hasData ? q.high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                  <div style={{ color: C.sub, fontSize: 11 }}>{hasData ? q.low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</div>
                </div>
                {alertsForSym.length > 0 && (
                  <div style={{ padding: '2px 10px 8px', display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {alertsForSym.map(a => {
                      const fired = firedAlerts.has(a.id);
                      return (
                        <span key={a.id} style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, background: fired ? 'rgba(14,203,129,0.1)' : 'rgba(234,179,8,0.1)', border: `1px solid ${fired ? 'rgba(14,203,129,0.3)' : 'rgba(234,179,8,0.3)'}`, color: fired ? C.greenBright : '#eab308' }}>
                          {fired ? '✓' : '🔔'} {a.direction === 'above' ? '↑' : '↓'} {a.price}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
